/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Grid, useTheme, Typography } from "@mui/material";
import {
  Message,
  Headline,
  InputChat,
  ChatModal,
  CustomModal,
  ChatNoMessages,
  MessageToBeReplied,
} from "~components/atoms";
import { SidebarChat } from "~components/Organisms";
import { DownloadIcon } from "~components/Icons";
import { useSocketContext } from "~contexts/Socket";
import type { IMessage } from "~contexts/Socket/interfaces";
import { getMessagesWithId } from "~helpers/chat";
import { getStyles } from "./styles";
import { useStreamContext } from "~contexts/Stream";
import BrandSelector from "~components/atoms/BrandSelector";

const Chat = () => {
  const theme: any = useTheme();
  const { messages } = useSocketContext();
  const { currentStream } = useStreamContext();
  const [messagesCount, setMessagesCount] = useState<number>(0);
  const [isModalOpened, setModalOpened] = useState<boolean>(false);
  const [isFirstAccess, setIsFirstAccess] = useState<boolean>(true);
  const [replyingTo, setReplyingTo] = useState<IMessage | null>(null);
  const [messagesWithId, setMessagesWithId] = useState<IMessage[]>([]);
  const [isRoomReachedBottom, setIsRoomReachedBottom] = useState<boolean>(true);
  const { styles } = getStyles(theme, replyingTo);

  const PRIMARY_BUTTON = {
    icon: <DownloadIcon />,
    text: "download do histórico",
    onAction: () => setModalOpened(true),
  };

  const onNavigateToLastMessage = () => {
    const lastMessageElement = document.querySelector("#last-message");

    if (lastMessageElement) {
      lastMessageElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  const onRoomScroll = (target: HTMLDivElement) => {
    if (target.scrollHeight - target.scrollTop === target.clientHeight) {
      setIsRoomReachedBottom(true);
    } else {
      setIsRoomReachedBottom(false);
    }
  };

  useEffect(() => {
    setMessagesWithId(getMessagesWithId(messages));
  }, [messages]);

  useEffect(() => {
    if (isRoomReachedBottom) {
      setMessagesCount(messagesWithId.length);
    }
  }, [isRoomReachedBottom]);

  useEffect(() => {
    if (messagesWithId.length > messagesCount && isRoomReachedBottom) {
      setIsRoomReachedBottom(false);
    }
  }, [messagesWithId, messagesCount]);

  useEffect(() => {
    const lastMessageElement = document.querySelector("#last-message");

    if (lastMessageElement && isFirstAccess) {
      lastMessageElement.scrollIntoView({ behavior: "smooth" });
      setIsFirstAccess(false);
      setMessagesCount(messagesWithId.length);
    }
  }, [messagesWithId, isFirstAccess]);

  // Se não há brand selecionada, mostrar mensagem
  if (!currentStream) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          minHeight: "60vh",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.grey[0],
          }}
          variant="h1"
        >
          bem-vindo ao ambiente AzzasLive
        </Typography>
        <Typography
          sx={{
            color: theme.palette.grey[600],
            fontSize: "15px",
            marginBottom: "24px",
          }}
        >
          comece a configurar sua live pelo menu ao lado após selecionar uma marca
        </Typography>
        <BrandSelector invertColors={true} />
      </Box>
    );
  }

  return (
    <Grid container sx={styles.wrapper}>
      <Grid item sx={styles.content}>
        <Headline
          hasDropdown
          title="chat"
          primaryButton={PRIMARY_BUTTON}
          description="aqui você acompanha as mensagens que estão rolando no chat da live"
        />

        <Box sx={styles.room} onScroll={(e) => onRoomScroll(e.target as HTMLDivElement)}>
          {messagesWithId !== null && !!messagesWithId.length ? (
            messagesWithId.map((message, index, arr) => (
              <Message
                key={index}
                message={message}
                messagesWithId={messagesWithId}
                setReplyingTo={setReplyingTo}
                id={index + 1 === arr.length ? "last-message" : ""}
              />
            ))
          ) : (
            <ChatNoMessages />
          )}
          {messagesWithId.length > messagesCount && !isRoomReachedBottom && !replyingTo && (
            <button style={styles.roomButton} onClick={() => onNavigateToLastMessage()}>
              <span style={styles.roomButtonCount}>
                {messagesWithId.length - messagesCount >= 99
                  ? "99+"
                  : messagesWithId.length - messagesCount}
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" fill="none">
                <path
                  stroke="#333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeMiterlimit="10"
                  d="m13.139 1.139-6 6-6-6"
                />
              </svg>
            </button>
          )}
        </Box>

        {replyingTo && (
          <>
            <MessageToBeReplied replyingTo={replyingTo} />
            <InputChat replyingTo={replyingTo} setReplyingTo={setReplyingTo} />
          </>
        )}

        {isModalOpened && (
          <CustomModal isOpened={isModalOpened} onClose={() => setModalOpened(false)}>
            <ChatModal setModalOpened={setModalOpened} />
          </CustomModal>
        )}
      </Grid>
      <SidebarChat />
    </Grid>
  );
};

export default Chat;
